import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import {
  TeamReportList,
  GameReportListItem,
  TeamReportListItem,
} from "../util/Requests";
import { useReportStore } from "../data/ReportStore";
import { Team } from "../models/Team";
import {
  useLeagueApi,
  useLeagueEnvironment,
  useLeaguePaths,
} from "../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../data/Cache";
import GamePicker, { PickerGame, PickerPlayer } from "../components/Elements/GamePicker/GamePicker";
import { customReportId, parseReportId } from "./useTeamReport";
import "./teamReportDesign.scss";

const seasonLabel = (season: number) =>
  `${season}-${((season + 1) % 100).toString().padStart(2, "0")}`;

const seasonReportLabel = (report: TeamReportListItem, playoff: boolean, preseason = false) => {
  if (report.preview) {
    return `${seasonLabel(report.season)} · Roster preview (${seasonLabel(report.season - 1)} stats)`;
  }
  if (preseason) return `${seasonLabel(report.season)} · Preseason`;
  return `${seasonLabel(report.season)} · ${playoff ? "Playoffs" : "Regular Season"}`;
};

type SeasonChoice = { report: TeamReportListItem; playoff: boolean; preseason: boolean };

// Window reports (Last N games, phase 2): a regular season's, as the backend builds them
const WINDOW_TYPES = ["L5", "L10", "L20"] as const;
type WindowType = (typeof WINDOW_TYPES)[number];
// Custom (2026-10-27): any of the season's games, built on request for any team (custom_windows.py)
type Scope = "season" | "game" | WindowType | "custom";
const WINDOW_LABELS: Record<WindowType, string> = { L5: "Last 5", L10: "Last 10", L20: "Last 20" };
/** A window report id's type and season: "POR-L10-2025" -> { type: "L10", season: 2025 } */
const windowOf = (id?: string | null) => {
  const type = WINDOW_TYPES.find((t) => id?.includes(`-${t}-`));
  return type ? { type, season: Number(id!.split("-").pop()) } : null;
};
/** A custom window report id's season: "POR-custom-<key>-2025" -> { season: 2025 } */
const customOf = (id?: string | null) => {
  const parsed = id ? parseReportId(id) : null;
  return parsed?.customKey && parsed.season ? { season: parsed.season } : null;
};

// A game belongs to the season report of its season and type: playoffs, preseason, else the regular season.
const gameInSeason = (g: GameReportListItem, { report, playoff, preseason }: SeasonChoice) =>
  g.gameInfo.season === report.season &&
  (playoff
    ? g.gameInfo.seasonType === "Playoffs"
    : preseason
      ? g.gameInfo.seasonType === "Preseason"
      : g.gameInfo.seasonType !== "Playoffs" && g.gameInfo.seasonType !== "Preseason");

const gameReportLabel = (report: GameReportListItem) => {
  const { opponent, date, isHome } = report.gameInfo;
  return `${dayjs(date).format("MMM D").toUpperCase()} · ${isHome ? "VS" : "@"} ${opponent.abbrev}`;
};

/**
 * The 1B web masthead: dark picker strip (team / season / scope / game +
 * View Report) with the "Viewing" context line under it. Web-only — print
 * hides the whole strip. Selection is staged locally and applied by the
 * View Report button; picking a different team navigates to that team's
 * report path. The picker starts on the report being viewed and follows it when another one opens (a link, a game
 * card), so it doubles as the report's heading (Trevor, 2026-10-19): a game report shows Single game, that game and
 * its season and type.
 */
export default function ReportPickerMasthead({
  reportList,
  selectedReportId,
  onReportChange,
  availableGameIds = [],
  loading = false,
  windowGameIds,
}: {
  reportList: TeamReportList;
  selectedReportId?: string | null;
  onReportChange?: (reportId: string) => void;
  availableGameIds?: string[];
  loading?: boolean;
  /** The viewed window report's games (`header.window.gameIds`): a custom report's picks */
  windowGameIds?: string[];
}) {
  const { teamAbbrev } = useParams<{ teamAbbrev?: string }>();
  const environment = useLeagueEnvironment();
  const api = useLeagueApi();
  const { getReportsTeamPath } = useLeaguePaths();
  const teams = useReportStore(
    (store) => store.teamsByEnvironment[environment] ?? [],
  );
  const sortedTeams = useMemo(
    () =>
      [...teams].sort((a: Team, b: Team) =>
        `${a.city} ${a.name}`.localeCompare(`${b.city} ${b.name}`),
      ),
    [teams],
  );

  const seasonReports: SeasonChoice[] = [
    ...reportList.season.map((r) => ({ report: r, playoff: false, preseason: false })),
    ...reportList.playoff_season.map((r) => ({ report: r, playoff: true, preseason: false })),
    ...(reportList.preseason_season ?? []).map((r) => ({ report: r, playoff: false, preseason: true })),
  ].sort((a, b) => b.report.season - a.report.season);

  const selectedGame = reportList.game.find((r) => r.id === selectedReportId);
  const selectedSeason = seasonReports.find(
    ({ report }) => report.id === selectedReportId,
  );
  const isGameReport = !!selectedReportId?.includes("-game-");
  const viewedWindow = windowOf(selectedReportId);
  const viewedCustom = customOf(selectedReportId);
  // A window report (Last N or custom) sits under its regular season
  const windowSeasonNum = viewedWindow?.season ?? viewedCustom?.season;
  const windowSeason = windowSeasonNum
    ? seasonReports.find((sel) => !sel.playoff && !sel.preseason && sel.report.season === windowSeasonNum)
    : undefined;

  // What the picker shows when nothing is staged: the report being viewed.
  const viewedTeam = teamAbbrev?.toUpperCase() ?? "";
  const viewedSeasonId =
    (selectedSeason ??
      windowSeason ??
      (selectedGame && seasonReports.find((sel) => !sel.report.preview && gameInSeason(selectedGame, sel))))
      ?.report.id ??
    seasonReports[0]?.report.id ??
    "";
  const viewedScope: Scope = isGameReport
    ? "game"
    : viewedWindow
      ? viewedWindow.type
      : viewedCustom
        ? "custom"
        : "season";
  const viewedGameId = selectedGame?.id ?? "";
  const viewedPicks = viewedCustom ? (windowGameIds ?? []).join(",") : "";

  // Staged (pending) selection — applied by View Report.
  const [pendingTeam, setPendingTeam] = useState<string>(viewedTeam);
  const [pendingSeasonId, setPendingSeasonId] = useState<string>(viewedSeasonId);
  const [pendingScope, setPendingScope] = useState<Scope>(viewedScope);
  const [pendingGameId, setPendingGameId] = useState<string>(viewedGameId);
  const [pendingPicks, setPendingPicks] = useState<string[]>(viewedPicks ? viewedPicks.split(",") : []);
  const [customError, setCustomError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  // A newly viewed report (or the list catching up with it: its game arrives with the schedule) resets the picker to it
  useEffect(() => {
    setPendingTeam(viewedTeam);
    setPendingSeasonId(viewedSeasonId);
    setPendingScope(viewedScope);
    setPendingGameId(viewedGameId);
    setPendingPicks(viewedPicks ? viewedPicks.split(",") : []);
    setCustomError(null);
  }, [viewedTeam, viewedSeasonId, viewedScope, viewedGameId, viewedPicks]);

  // The game dropdown only offers games from the season/type picked above —
  // no mixing playoffs into a regular-season selection.
  const pendingSeasonSel = seasonReports.find(
    ({ report }) => report.id === pendingSeasonId,
  );
  const gameReports = reportList.game.filter((g) =>
    pendingSeasonSel ? gameInSeason(g, pendingSeasonSel) : true,
  );
  // The windows the picked regular season has reports for
  const windowScopes = WINDOW_TYPES.filter(
    (t) =>
      pendingSeasonSel &&
      !pendingSeasonSel.playoff &&
      !pendingSeasonSel.preseason &&
      (reportList[t] ?? []).some((r) => r.season === pendingSeasonSel.report.season),
  );

  const currentTeam = sortedTeams.find(
    (team) => team.abbrev.toUpperCase() === teamAbbrev?.toUpperCase(),
  );

  // Custom: the regular season's games of the team being viewed (another staged team opens at its default report)
  const customSeason =
    environment === ExplorerEnvironment.NBA &&
    pendingSeasonSel &&
    !pendingSeasonSel.playoff &&
    !pendingSeasonSel.preseason &&
    !pendingSeasonSel.report.preview
      ? pendingSeasonSel.report.season
      : null;
  const showCustomPicker = pendingScope === "custom" && customSeason !== null && pendingTeam === viewedTeam;
  const [customGames, setCustomGames] = useState<{
    key: string;
    games: PickerGame[];
    players: PickerPlayer[];
  } | null>(null);
  const customGamesKey = currentTeam && customSeason !== null ? `${currentTeam.abbrev}-${customSeason}` : null;
  useEffect(() => {
    if (!showCustomPicker || !currentTeam || customSeason === null || customGames?.key === customGamesKey) return;
    let cancelled = false;
    api
      .getCustomReportGames(currentTeam, customSeason)
      .then(({ games, players }) => {
        if (!cancelled) setCustomGames({ key: customGamesKey as string, games, players });
      })
      .catch((err) => console.error("Error fetching the custom games:", err));
    return () => {
      cancelled = true;
    };
  }, [api, showCustomPicker, currentTeam, customSeason, customGamesKey, customGames?.key]);

  const viewingLabel = (() => {
    const teamLabel = currentTeam
      ? `${currentTeam.city} ${currentTeam.name}`
      : (teamAbbrev ?? "");
    const reportLabel = selectedGame
      ? `${gameReportLabel(selectedGame)} · ${dayjs(selectedGame.gameInfo.date).format("YYYY")}`
      : selectedSeason
        ? seasonReportLabel(selectedSeason.report, selectedSeason.playoff, selectedSeason.preseason)
        : viewedWindow && windowSeason
          ? `${seasonLabel(windowSeason.report.season)} · ${WINDOW_LABELS[viewedWindow.type]}`
          : viewedCustom
            ? `${seasonLabel(viewedCustom.season)} · Custom${
                windowGameIds?.length ? ` · ${windowGameIds.length} ${windowGameIds.length === 1 ? "game" : "games"}` : ""
              }`
            : "";
    return [teamLabel, reportLabel].filter(Boolean).join(" · ").toUpperCase();
  })();

  const handleView = () => {
    const targetAbbrev = pendingTeam || teamAbbrev?.toUpperCase() || "";
    if (targetAbbrev && targetAbbrev !== teamAbbrev?.toUpperCase()) {
      // Cross-team navigation: report ids are per-team, so land on the
      // team's default report.
      window.location.href = getReportsTeamPath(targetAbbrev);
      return;
    }
    if (pendingScope === "custom") {
      if (!currentTeam || customSeason === null || !pendingPicks.length) return;
      setRequesting(true);
      setCustomError(null);
      api
        .requestCustomReport(currentTeam, customSeason, pendingPicks)
        .then(({ key }) => {
          const id = customReportId(currentTeam.abbrev.toUpperCase(), key, customSeason);
          if (id !== selectedReportId) onReportChange?.(id);
        })
        .catch((err) =>
          setCustomError(err?.response?.data?.error ?? "Could not request the custom report."),
        )
        .finally(() => setRequesting(false));
      return;
    }
    const targetId =
      pendingScope === "game"
        ? pendingGameId
        : pendingScope === "season"
          ? pendingSeasonId
          : pendingSeasonSel
            ? `${teamAbbrev?.toUpperCase()}-${pendingScope}-${pendingSeasonSel.report.season}`
            : "";
    if (targetId && targetId !== selectedReportId) {
      onReportChange?.(targetId);
    }
  };

  return (
    <div className="trd trd-webonly">
      <div className="picker">
        <div className="brand">
          {/* The org's own black-pinwheel asset inverted to white — its
              stripe gaps are transparent, so the invert keeps the pinwheel
              shape (the CDN team logo renders as a solid blob when
              inverted, and the design project's white PNG exceeds the
              256KiB design-file read cap). */}
          <img
            src="/images/blazers-logo-black.png"
            alt="Portland Trail Blazers"
            style={{ filter: "invert(1)" }}
          />
          <span>
            Team
            <br />
            Reports
          </span>
        </div>
        {sortedTeams.length > 0 && (
          <div className="fld">
            <label htmlFor="pk-team">Team</label>
            <select
              id="pk-team"
              value={pendingTeam}
              disabled={loading}
              onChange={(e) => setPendingTeam(e.target.value)}
            >
              {sortedTeams.map((team) => (
                <option key={team.abbrev} value={team.abbrev.toUpperCase()}>
                  {team.city} {team.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="fld">
          <label htmlFor="pk-season">Season</label>
          <select
            id="pk-season"
            value={pendingSeasonId}
            disabled={loading}
            onChange={(e) => {
              setPendingSeasonId(e.target.value);
              setPendingGameId("");
            }}
          >
            {seasonReports.map(({ report, playoff, preseason }) => (
              <option key={report.id} value={report.id}>
                {seasonReportLabel(report, playoff, preseason)}
              </option>
            ))}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="pk-scope">Scope</label>
          <select
            id="pk-scope"
            value={pendingScope}
            disabled={loading}
            onChange={(e) => setPendingScope(e.target.value as Scope)}
          >
            <option value="season">Full season</option>
            {windowScopes.map((t) => (
              <option key={t} value={t}>
                {WINDOW_LABELS[t]}
              </option>
            ))}
            <option value="game">Single game</option>
            {customSeason !== null && <option value="custom">Custom games</option>}
          </select>
        </div>
        {pendingScope === "game" && (
          <div className="fld">
            <label htmlFor="pk-game">Game</label>
            <select
              id="pk-game"
              value={pendingGameId}
              disabled={loading}
              onChange={(e) => setPendingGameId(e.target.value)}
            >
              <option value="">
                Choose game — {gameReports.length} played
              </option>
              {gameReports.map((report) => {
                const processing = !availableGameIds.includes(
                  report.gameInfo.gameId,
                );
                return (
                  <option
                    key={report.id}
                    value={report.id}
                    disabled={processing}
                  >
                    {gameReportLabel(report)}
                    {processing ? " (Processing)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        <button
          className="go"
          type="button"
          disabled={
            loading ||
            requesting ||
            (pendingScope === "game" && !pendingGameId) ||
            (pendingScope === "custom" && !pendingPicks.length)
          }
          onClick={handleView}
        >
          View Report
        </button>
      </div>
      {showCustomPicker && (
        <div className="custom-games">
          {customGames?.key === customGamesKey ? (
            <GamePicker
              games={customGames!.games}
              players={customGames!.players}
              picked={pendingPicks}
              onChange={setPendingPicks}
            />
          ) : (
            <div className="custom-games-note">Loading the season's games…</div>
          )}
          {customError && <div className="custom-games-note error">{customError}</div>}
        </div>
      )}
      <div className="viewing">
        <span className="vk">Viewing</span>
        <b>{viewingLabel}</b>
      </div>
    </div>
  );
}
